import type { PayrollRunCalculation } from '../types';
import { TAX_WARNINGS_INTRO, TAX_WARNINGS_LOAD_FAILED, TAX_WARNINGS_LOADING, TAX_WARNINGS_TITLE } from '../reviewCopy';

export type TaxWarningsState =
  | { kind: 'loading' }
  | { kind: 'ready'; calculations: PayrollRunCalculation[] }
  | { kind: 'error' };

/**
 * EPN-22: the tax engine's per-employee warnings, saved on each calculation and snapshotted onto the run when it was attached. Informational
 * only: it never blocks approval (the server's tax-readiness gate is separate, see TaxReadinessNotice). Shows EPN's code and EPN's own wording,
 * never vendor text. Nothing is shown when no calculation has a warning. No Figma frame exists for this state (UX / DESIGN GAP); it follows the
 * page's existing panel pattern as a labelled note, not an alert.
 */
export function TaxWarningsNotice({
  state, employeeNames, onRetry,
}: { state: TaxWarningsState; employeeNames: ReadonlyMap<string, string>; onRetry: () => void }) {
  if (state.kind === 'loading') {
    return <p className="muted" role="status">{TAX_WARNINGS_LOADING}</p>;
  }
  if (state.kind === 'error') {
    return (
      <div className="payroll-review__panel" role="status">
        <p className="muted">{TAX_WARNINGS_LOAD_FAILED}</p>
        <button type="button" onClick={onRetry}>Retry tax warnings</button>
      </div>
    );
  }

  const withWarnings = state.calculations.filter((c) => (c.taxWarnings?.length ?? 0) > 0);
  if (withWarnings.length === 0) return null;
  return (
    <section className="payroll-review__panel" role="note" aria-labelledby="payroll-review-tax-warnings-title">
      <strong id="payroll-review-tax-warnings-title">{TAX_WARNINGS_TITLE}</strong>
      <p className="muted">{TAX_WARNINGS_INTRO}</p>
      <ul>
        {withWarnings.map((c) => {
          const name = employeeNames.get(c.employeeId) ?? `Employee ${c.employeeId}`;
          return (
            <li key={c.id}>
              <span>{name}</span>
              <ul aria-label={`Tax warnings for ${name}`}>
                {c.taxWarnings!.map((w) => (
                  <li key={w.code}>
                    {w.message} <span className="muted">({w.code})</span>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
